import { addDays, daysBetween, parseDay, todayStr, weekKey } from "./date";
import type { KayaState } from "./types";

/**
 * Trend weight: the number under the daily noise.
 *
 * A scale reading swings ±0.5–1 kg a day on water, salt and what is still in the gut, while a
 * sustainable loss is ~0.07 kg a day. Any single reading, and any fit that treats every reading
 * as equally true, mostly measures the noise. An exponentially smoothed trend (The Hacker's Diet,
 * TrendWeight, Libra) is the standard answer and stays honest when weigh-ins are sparse.
 */

export interface Pt {
  date: string;
  v: number;
}

/** ~10-day memory: long enough to flatten a salty dinner, short enough to show a stall within
 *  two weeks. The Hacker's Diet default. */
export const WEIGHT_ALPHA = 0.1;
/** Waist is measured weekly and moves less on water, so it can afford to follow readings faster. */
export const WAIST_ALPHA = 0.2;

/** Past this the date means nothing: the body adapts long before, and a year-out date reads as
 *  a promise. Research item 4 — Libra caps at six months; a year keeps a slow-but-real trend. */
const ETA_MAX_DAYS = 365;
/** Below this the "rate" is scale noise, not a direction; a target date from it would be decades. */
const MIN_LOSS_KG_PER_WEEK = 0.02;
/** The CDC/ACSM-cited sustainable ceiling, as a fraction of bodyweight per week. */
const FAST_FRACTION_PER_WEEK = 0.01;

function series(S: KayaState, pick: (m: KayaState["measures"][number]) => number | undefined): Pt[] {
  const byDate = new Map<string, number>();
  for (const m of S.measures) {
    const v = pick(m);
    if (typeof v === "number" && Number.isFinite(v) && v > 0) byDate.set(m.date, v);
  }
  return [...byDate.entries()]
    .map(([date, v]) => ({ date, v }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
}

export function weightSeries(S: KayaState): Pt[] {
  return series(S, (m) => m.w);
}

export function waistSeries(S: KayaState): Pt[] {
  return series(S, (m) => m.waist);
}

/**
 * The trend value at each observation. Gap-aware: a reading after `n` days moves the trend as
 * much as `n` daily readings would have, so a weekly weigher is not smoothed into a standstill
 * and a daily one is not over-weighted.
 */
export function ewma(pts: Pt[], alpha = WEIGHT_ALPHA): Pt[] {
  const out: Pt[] = [];
  let prev: Pt | null = null;
  for (const p of pts) {
    if (!prev) {
      prev = { date: p.date, v: p.v };
    } else {
      const dt = Math.max(0, daysBetween(prev.date, p.date));
      const a = 1 - Math.pow(1 - alpha, dt);
      prev = { date: p.date, v: prev.v + a * (p.v - prev.v) };
    }
    out.push(prev);
  }
  return out;
}

/**
 * Change per week over the recent window, from a least-squares line through the trend.
 *
 * Only the window, not the whole history: the first fortnight of any diet is mostly water, and a
 * fit over everything since day one lets that dominate forever. `asOf` anchors the window (the
 * last point by default); anchoring on today makes a lapsed weigh-in habit read as "not enough",
 * not as a rate from two months ago.
 */
export function trendRate(trend: Pt[], windowDays = 21, asOf?: string): number | null {
  if (!trend.length) return null;
  const end = asOf ?? trend[trend.length - 1].date;
  const pts = trend.filter((p) => {
    const age = daysBetween(p.date, end);
    return age >= 0 && age <= windowDays;
  });
  if (pts.length < 3) return null;
  const x0 = pts[0].date;
  const xs = pts.map((p) => daysBetween(x0, p.date));
  if (xs[xs.length - 1] - xs[0] < 10) return null;
  const n = pts.length;
  const mx = xs.reduce((a, b) => a + b, 0) / n;
  const my = pts.reduce((a, p) => a + p.v, 0) / n;
  let num = 0;
  let den = 0;
  xs.forEach((x, i) => {
    num += (x - mx) * (pts[i].v - my);
    den += (x - mx) ** 2;
  });
  return den ? (num / den) * 7 : null;
}

/** Mean of the readings in each Monday-first week. */
export function weeklyAverages(pts: Pt[]): { week: string; mean: number; n: number }[] {
  const acc = new Map<string, { sum: number; n: number }>();
  for (const p of pts) {
    const k = weekKey(parseDay(p.date));
    const a = acc.get(k) ?? { sum: 0, n: 0 };
    acc.set(k, { sum: a.sum + p.v, n: a.n + 1 });
  }
  return [...acc.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([week, a]) => ({ week, mean: a.sum / a.n, n: a.n }));
}

/** When the trend would cross the weight target if the current rate held — or null when any
 *  honest reading of the numbers says there is no such date. */
export function etaToTarget(
  S: KayaState,
  trendNow: number,
  ratePerWeek: number | null,
  from: string = todayStr()
): { days: number; date: string } | null {
  const target = S.profile.targetWeight;
  if (S.profile.goal !== "weight" || !target) return null;
  if (ratePerWeek === null || ratePerWeek >= -MIN_LOSS_KG_PER_WEEK) return null;
  if (!(trendNow > target)) return null;
  const days = Math.round((trendNow - target) / (-ratePerWeek / 7));
  if (days > ETA_MAX_DAYS) return null;
  return { days, date: addDays(from, days) };
}

/** The trend carried forward to `ds` at `ratePerWeek`. Counted from the trend point itself, not
 *  from today: a fortnight-old weigh-in would otherwise push the line a fortnight too far. */
export function projectTrend(from: Pt, ratePerWeek: number, ds: string): Pt {
  return { date: ds, v: from.v + (ratePerWeek / 7) * daysBetween(from.date, ds) };
}

export function compareRates(
  observed: number | null,
  predicted: number | null
): { observed: number | null; predicted: number | null; gap: number | null } {
  return {
    observed,
    predicted,
    gap: observed === null || predicted === null ? null : observed - predicted,
  };
}

/**
 * Losing faster than 1% of bodyweight a week, going by the trend over the last week or so.
 *
 * Measured on the trend, not raw readings, so one dry morning after a wet one cannot trip it.
 * The comparison point must be 5–14 days back: nearer is noise, further is no longer "this week".
 */
export function tooFast(S: KayaState, trend: Pt[]): boolean {
  if (trend.length < 2) return false;
  const last = trend[trend.length - 1];
  let ref: Pt | null = null;
  for (let i = trend.length - 2; i >= 0; i--) {
    const age = daysBetween(trend[i].date, last.date);
    if (age >= 5 && age <= 14) {
      if (!ref || Math.abs(age - 7) < Math.abs(daysBetween(ref.date, last.date) - 7)) ref = trend[i];
    }
    if (age > 14) break;
  }
  if (!ref) return false;
  const lossPerWeek = ((ref.v - last.v) / daysBetween(ref.date, last.date)) * 7;
  return lossPerWeek > last.v * FAST_FRACTION_PER_WEEK;
}

/* ---------- the goal date as a range, the pace, the distance covered (ADR-033) ---------- */

/** A goal date needs a fortnight of weigh-ins and eight of them: before that the first weeks'
 *  water dominates any slope (research §2.1 G1). */
export const ETA_MIN_SPAN_DAYS = 14;
export const ETA_MIN_WEIGH_INS = 8;
/** The window the second rate is read over — "the last 8 weeks". */
const ETA_LONG_WINDOW = 56;
/** The narrowest the range may be, as a share of the rate either way. A real person's weekly
 *  rate swings by about this much, so a range narrower than ±20 % would claim a precision that
 *  two good weeks and one salty one would break. */
const ETA_MIN_SPREAD = 0.2;

export interface EtaRange {
  /** The date at the current (21-day) rate. */
  likely: string;
  /** The earliest and latest plausible dates. */
  from: string;
  to: string;
  days: number;
  /** True when the slow end ran past a year and was cut there. */
  capped: boolean;
}

/**
 * The goal date as a range, counted from `from` — the logged reading on screen (ADR-029 §9).
 *
 * The range is the spread between the current rate (21 days) and the rate over the last eight
 * weeks, widened to at least ±20 % of the current rate. Chosen over a standard deviation of
 * weekly rates because weekly rates from two or three weigh-ins a week are mostly noise: their
 * spread made a range of months. The two-window rule answers the question the range is for —
 * "has the pace been changing?" — and the floor covers the ordinary week-to-week wobble.
 */
export function etaRange(
  trend: Pt[],
  target: number,
  asOf: string,
  rate: number | null,
  from?: Pt
): EtaRange | null {
  if (trend.length < ETA_MIN_WEIGH_INS) return null;
  if (daysBetween(trend[0].date, trend[trend.length - 1].date) < ETA_MIN_SPAN_DAYS) return null;
  if (rate === null || rate >= -MIN_LOSS_KG_PER_WEEK) return null;
  const start = from ?? trend[trend.length - 1];
  if (!(start.v > target)) return null;
  const daysAt = (r: number) => Math.round((start.v - target) / (-r / 7));
  const likely = daysAt(rate);
  if (likely > ETA_MAX_DAYS) return null;
  const rates = [rate, rate * (1 + ETA_MIN_SPREAD), rate * (1 - ETA_MIN_SPREAD)];
  const long = trendRate(trend, ETA_LONG_WINDOW, asOf);
  if (long !== null) rates.push(long);
  const fast = Math.min(...rates);
  const slow = Math.max(...rates);
  const slowDays = slow < -MIN_LOSS_KG_PER_WEEK ? daysAt(slow) : Infinity;
  const toDays = Math.min(slowDays, ETA_MAX_DAYS);
  return {
    likely: addDays(start.date, likely),
    from: addDays(start.date, Math.min(daysAt(fast), likely)),
    to: addDays(start.date, Math.max(toDays, likely)),
    days: likely,
    capped: slowDays > ETA_MAX_DAYS,
  };
}

export type PaceBand = "fast" | "inside" | "under" | "slow" | "flat" | "gaining";

export interface PaceVerdict {
  /** Loss as a percentage of body weight a week; negative is a gain. */
  pct: number;
  band: PaceBand;
  /** The clause after the figure: "inside the usual 0.5–1 % range". */
  words: string;
  /** Under three weeks of weigh-ins: a fast start is glycogen water, not a warning. */
  early: boolean;
}

export const PACE_WORDS: Record<PaceBand, string> = {
  fast: "over the usual 1 % ceiling — faster than most plans",
  inside: "inside the usual 0.5–1 % range",
  under: "just under the usual 0.5–1 % range — steady and sustainable",
  slow: "slower than the usual 0.5–1 % range — the trend is barely moving",
  flat: "holding — under 0.1 % a week either way",
  gaining: "gaining",
};

/**
 * The trend's rate as a share of body weight a week, and where that sits against the usual
 * 0.5–1 % (research G2). Each band names the range honestly: 0.46 % is "just under" it, not
 * "inside" it. Below 0.1 % either way the trend is holding, and a gain is named as one.
 */
export function paceVerdict(S: KayaState, rate: number | null, weight: number | null): PaceVerdict | null {
  if (rate === null || !weight) return null;
  const pct = (-rate / weight) * 100;
  const band: PaceBand =
    pct >= FAST_FRACTION_PER_WEEK * 100
      ? "fast"
      : pct >= 0.5
        ? "inside"
        : pct >= 0.25
          ? "under"
          : pct >= 0.1
            ? "slow"
            : pct > -0.1
              ? "flat"
              : "gaining";
  const w = weightSeries(S);
  const early = w.length < 2 || daysBetween(w[0].date, w[w.length - 1].date) < 21;
  return { pct, band, words: PACE_WORDS[band], early };
}

export interface LostSoFar {
  /** Start weight less the latest reading; negative is a gain. */
  kg: number;
  /** The whole distance, start to target — null without a weight goal. */
  of: number | null;
  /** Share of `of` covered, 0–100, or null. */
  pct: number | null;
  /** Whole weeks from the plan's start to the reading. */
  weeks: number;
  since: string;
}

/** "3.4 of 8 kg gone in 12 weeks" — counted from the reading, so it agrees with the hero (G4). */
export function lostSoFar(S: KayaState): LostSoFar | null {
  const w = weightSeries(S);
  if (!w.length) return null;
  const latest = w[w.length - 1];
  const start = S.profile.startWeight || w[0].v;
  const since = S.profile.startDate && S.profile.startDate <= latest.date ? S.profile.startDate : w[0].date;
  const target = S.profile.goal === "weight" ? S.profile.targetWeight : 0;
  const of = target && start > target ? start - target : null;
  const kg = start - latest.v;
  return {
    kg,
    of,
    pct: of === null ? null : Math.max(0, Math.min(100, (kg / of) * 100)),
    weeks: Math.round(daysBetween(since, latest.date) / 7),
    since,
  };
}
